// src/pages/dashboard/NotificationSettings.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Mail, Monitor, AlertCircle, CheckCircle, MessageSquare } from 'lucide-react';
import { useToast } from '../../components/ToastNotification';
import Toggle from '../../components/Toggle';
import firestoreService from '../../services/firestore-service';
import authService from '../../services/auth-service';
import { db } from '../../config/firebase-config';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

const NotificationSettings = () => {
  const toast = useToast();

  // User role detection
  const userRole = localStorage.getItem('userRole') || 'client';
  const isPractitioner = userRole === 'practitioner';

  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Practice Coach email frequency
  const [practiceCoachFrequency, setPracticeCoachFrequency] = useState('weekly');
  const [coachFrequencySaving, setCoachFrequencySaving] = useState(false);
  const [coachFrequencySaved, setCoachFrequencySaved] = useState(false);

  // Global toggles
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [inAppEnabled, setInAppEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(true);

  // Default preferences structure
  const defaultPreferences = {
    // Message notifications (CRITICAL - cannot disable)
    messages: { email: true, inApp: true },

    // Booking notifications
    newBooking: { email: true, inApp: true },
    bookingCancelled: { email: true, inApp: true },
    bookingRescheduled: { email: true, inApp: true },

    // Session notifications
    sessionReminder24h: { email: true, inApp: false },
    sessionReminder1h: { email: true, inApp: false },
    actionItemsAssigned: { email: true, inApp: true },
    actionItemsCompleted: { email: false, inApp: true },

    // Payment notifications (practitioners only)
    paymentReceived: { email: true, inApp: true },
    payoutProcessed: { email: true, inApp: false },

    // Review notifications
    reviewRequest: { email: false, inApp: false }
  };

  const [preferences, setPreferences] = useState(defaultPreferences);

  // Load preferences from Firestore on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    setLoading(true);

    try {
      // Load from localStorage first (fast)
      const savedPrefs = localStorage.getItem('notificationPreferences');
      if (savedPrefs) {
        try {
          const parsed = JSON.parse(savedPrefs);
          setEmailEnabled(parsed.emailEnabled ?? true);
          setInAppEnabled(parsed.inAppEnabled ?? true);
          setSmsEnabled(parsed.smsEnabled ?? true);
          setPreferences(parsed.preferences || defaultPreferences);
          setLastUpdated(parsed.updatedAt ? new Date(parsed.updatedAt) : null);
        } catch (error) {
          console.error('Error parsing localStorage preferences:', error);
          // Continue to try Firestore fallback
        }
      }

      // Background: sync from Firestore if localStorage is empty
      if (!savedPrefs) {
        const userId = firestoreService.getCurrentUserId();
        if (userId) {
          try {
            const firestoreData = await firestoreService.getNotificationPreferences(userId);
            if (firestoreData && Object.keys(firestoreData).length > 0) {
              localStorage.setItem('notificationPreferences', JSON.stringify(firestoreData));
              setEmailEnabled(firestoreData.emailEnabled ?? true);
              setInAppEnabled(firestoreData.inAppEnabled ?? true);
              setSmsEnabled(firestoreData.smsEnabled ?? true);
              setPreferences(firestoreData.preferences || defaultPreferences);
              setLastUpdated(firestoreData.updatedAt ? new Date(firestoreData.updatedAt) : null);
            }
          } catch (err) {
            console.warn('Firestore notification prefs read failed:', err);
            // Use defaults
          }
        }
      }
      // Load Practice Coach frequency from Firestore (independent of notification prefs)
      const userId = firestoreService.getCurrentUserId();
      if (userId && db) {
        try {
          const { doc: firestoreDoc, getDoc } = await import('firebase/firestore');
          const snap = await getDoc(firestoreDoc(db, 'practitioners', userId));
          if (snap.exists()) {
            const freq = snap.data()?.practiceCoachSettings?.emailFrequency;
            if (freq) setPracticeCoachFrequency(freq);
          }
        } catch (err) {
          console.warn('NotificationSettings: failed to load practiceCoachSettings:', err.message);
        }
      }
    } catch (error) {
      console.error('Unexpected error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePracticeCoachFrequency = async (freq) => {
    setPracticeCoachFrequency(freq);
    setCoachFrequencySaved(false);
    setCoachFrequencySaving(true);
    const userId = firestoreService.getCurrentUserId();
    if (userId && db) {
      try {
        const { doc: firestoreDoc, setDoc, serverTimestamp: sTs } = await import('firebase/firestore');
        await setDoc(firestoreDoc(db, 'practitioners', userId), {
          practiceCoachSettings: { emailFrequency: freq, updatedAt: sTs() },
        }, { merge: true });
        setCoachFrequencySaved(true);
        setTimeout(() => setCoachFrequencySaved(false), 2000);
      } catch (err) {
        console.warn('NotificationSettings: failed to save practiceCoachSettings:', err.message);
      }
    }
    setCoachFrequencySaving(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const user = authService.getCurrentUser();

      if (!user) {
        toast.error('Error', 'No user found');
        return;
      }

      const data = {
        userId: user.uid || user.email,
        emailEnabled,
        inAppEnabled,
        smsEnabled,
        preferences,
        updatedAt: new Date()
      };

      // Save to localStorage (instant)
      localStorage.setItem('notificationPreferences', JSON.stringify(data));

      // Background sync to Firestore
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.saveNotificationPreferences(userId, data).then(() => {
          console.log('✅ Notification preferences saved to Firestore');
        }).catch(err => {
          console.warn('⚠️ Firestore notification prefs save failed:', err);
        });

        // Also write settings.smsEnabled to practitioners doc so Cloud Functions can read it
        if (db) {
          setDoc(doc(db, 'practitioners', userId), {
            settings: { smsEnabled },
            updatedAt: serverTimestamp(),
          }, { merge: true }).catch(err => {
            console.warn('⚠️ Firestore smsEnabled save failed:', err);
          });
        }
      }

      setLastUpdated(new Date());
      setHasChanges(false);
      toast.success('Saved', 'Notification preferences updated');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Error', 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handlePreferenceToggle = (category, channel, checked) => {
    // Don't allow toggling critical notifications
    if (category === 'messages' || category === 'newBooking') {
      return;
    }

    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [channel]: checked
      }
    }));
    setHasChanges(true);
  };

  const formatTimestamp = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header with Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
            <Link
              to="/dashboard/practitioner"
              className="text-purple-600 hover:text-purple-700 font-medium transition-colors"
            >
              Dashboard
            </Link>
            <span className="text-gray-400">/</span>
            <Link
              to="/dashboard/settings"
              className="text-purple-600 hover:text-purple-700 font-medium transition-colors"
            >
              Settings
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Notifications</span>
          </nav>

          {/* Page Title */}
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Bell className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Notification Preferences</h1>
              <p className="text-sm text-slate-600 mt-1">Choose how and when you receive notifications</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">

          {/* Global Toggles */}
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Master Controls</h2>

            {/* Email Notifications Toggle */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-purple-200">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-gray-900">Email Notifications</p>
                  <p className="text-sm text-gray-600">Receive notifications via email</p>
                </div>
              </div>
              <Toggle
                enabled={emailEnabled}
                onChange={(value) => {
                  setEmailEnabled(value);
                  setHasChanges(true);
                }}
              />
            </div>

            {/* In-App Notifications Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Monitor className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-gray-900">In-App Notifications</p>
                  <p className="text-sm text-gray-600">See notification badges and alerts in Divine Frameworks</p>
                </div>
              </div>
              <Toggle
                enabled={inAppEnabled}
                onChange={(value) => {
                  setInAppEnabled(value);
                  setHasChanges(true);
                }}
              />
            </div>

            {/* Warning messages when disabled */}
            {!emailEnabled && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-700">You won't receive any email notifications</p>
              </div>
            )}

            {!inAppEnabled && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-700">You won't see notification badges</p>
              </div>
            )}
          </div>

          {/* Client SMS Notifications */}
          {isPractitioner && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-2.5 bg-purple-50 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Client SMS Notifications</h2>
                  <p className="text-sm text-slate-600 mt-0.5">Send text messages to clients who provide a phone number during booking</p>
                </div>
              </div>

              <div className="flex items-center justify-between py-3 border-t border-gray-100">
                <div>
                  <p className="text-sm font-medium text-gray-900">SMS confirmations and reminders</p>
                  <p className="text-sm text-gray-500 mt-0.5">Booking confirmations, 24-hour reminders, and cancellation notices</p>
                </div>
                <Toggle
                  enabled={smsEnabled}
                  onChange={(value) => {
                    setSmsEnabled(value);
                    setHasChanges(true);
                  }}
                />
              </div>

              {!smsEnabled && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-700">Your clients will not receive SMS notifications</p>
                </div>
              )}

              {smsEnabled && (
                <p className="mt-3 text-xs text-gray-400">
                  SMS is only sent when clients provide a phone number. Standard message rates may apply to recipients.
                </p>
              )}
            </div>
          )}

          {/* Compact Always-Enabled Notice */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
              <p className="text-gray-700">
                <span className="font-medium">Always enabled:</span> New messages and booking confirmations (email + in-app)
              </p>
            </div>
          </div>

          {/* Booking Notifications */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Booking Notifications</h2>

            <div className="space-y-4">
              {/* Booking Cancelled */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900 mb-1">Booking cancelled</h4>
                  <p className="text-sm text-gray-600">Get notified when a booking is cancelled</p>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.bookingCancelled.email}
                      onChange={(e) => handlePreferenceToggle('bookingCancelled', 'email', e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Email</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.bookingCancelled.inApp}
                      onChange={(e) => handlePreferenceToggle('bookingCancelled', 'inApp', e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">In-App</span>
                  </label>
                </div>
              </div>

              {/* Booking Rescheduled */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900 mb-1">Booking rescheduled</h4>
                  <p className="text-sm text-gray-600">Get notified when a booking is rescheduled</p>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.bookingRescheduled.email}
                      onChange={(e) => handlePreferenceToggle('bookingRescheduled', 'email', e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Email</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.bookingRescheduled.inApp}
                      onChange={(e) => handlePreferenceToggle('bookingRescheduled', 'inApp', e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">In-App</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Session Notifications */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Session Notifications</h2>

            <div className="space-y-4">
              {/* 24-hour reminder */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900 mb-1">Session reminder (24 hours before)</h4>
                  <p className="text-sm text-gray-600">Get reminded one day before your session</p>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.sessionReminder24h.email}
                      onChange={(e) => handlePreferenceToggle('sessionReminder24h', 'email', e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Email</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.sessionReminder24h.inApp}
                      onChange={(e) => handlePreferenceToggle('sessionReminder24h', 'inApp', e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">In-App</span>
                  </label>
                </div>
              </div>

              {/* 1-hour reminder */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900 mb-1">Session reminder (1 hour before)</h4>
                  <p className="text-sm text-gray-600">Get reminded one hour before your session</p>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.sessionReminder1h.email}
                      onChange={(e) => handlePreferenceToggle('sessionReminder1h', 'email', e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Email</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.sessionReminder1h.inApp}
                      onChange={(e) => handlePreferenceToggle('sessionReminder1h', 'inApp', e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">In-App</span>
                  </label>
                </div>
              </div>

              {/* Action Items Assigned (Clients only) */}
              {!isPractitioner && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="mb-3">
                    <h4 className="font-medium text-gray-900 mb-1">Action items assigned</h4>
                    <p className="text-sm text-gray-600">Get notified when your practitioner assigns action items</p>
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.actionItemsAssigned.email}
                        onChange={(e) => handlePreferenceToggle('actionItemsAssigned', 'email', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Email</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.actionItemsAssigned.inApp}
                        onChange={(e) => handlePreferenceToggle('actionItemsAssigned', 'inApp', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">In-App</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Action Items Completed (Practitioners only) */}
              {isPractitioner && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="mb-3">
                    <h4 className="font-medium text-gray-900 mb-1">Action items completed</h4>
                    <p className="text-sm text-gray-600">Get notified when clients complete action items</p>
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.actionItemsCompleted.email}
                        onChange={(e) => handlePreferenceToggle('actionItemsCompleted', 'email', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Email</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.actionItemsCompleted.inApp}
                        onChange={(e) => handlePreferenceToggle('actionItemsCompleted', 'inApp', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">In-App</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Notifications (Practitioners Only) */}
          {isPractitioner && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Payment Notifications</h2>

              <div className="space-y-4">
                {/* Payment Received */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="mb-3">
                    <h4 className="font-medium text-gray-900 mb-1">Payment received</h4>
                    <p className="text-sm text-gray-600">Get notified when you receive a payment</p>
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.paymentReceived.email}
                        onChange={(e) => handlePreferenceToggle('paymentReceived', 'email', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Email</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.paymentReceived.inApp}
                        onChange={(e) => handlePreferenceToggle('paymentReceived', 'inApp', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">In-App</span>
                    </label>
                  </div>
                </div>

                {/* Payout Processed */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="mb-3">
                    <h4 className="font-medium text-gray-900 mb-1">Payout processed</h4>
                    <p className="text-sm text-gray-600">Get notified when payouts are processed to your account</p>
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.payoutProcessed.email}
                        onChange={(e) => handlePreferenceToggle('payoutProcessed', 'email', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Email</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.payoutProcessed.inApp}
                        onChange={(e) => handlePreferenceToggle('payoutProcessed', 'inApp', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">In-App</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Review Notifications */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Review Notifications</h2>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="mb-3">
                <h4 className="font-medium text-gray-900 mb-1">Review request</h4>
                <p className="text-sm text-gray-600">Get notified when you can leave or receive reviews</p>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.reviewRequest.email}
                    onChange={(e) => handlePreferenceToggle('reviewRequest', 'email', e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Email</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.reviewRequest.inApp}
                    onChange={(e) => handlePreferenceToggle('reviewRequest', 'inApp', e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">In-App</span>
                </label>
              </div>
            </div>
          </div>

          {/* Practice Coach Insights */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-semibold text-slate-900">Practice Coach Insights</h2>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#7C3AED', background: '#F5F3FF', borderRadius: '10px', padding: '1px 8px' }}>AI</span>
            </div>
            <p className="text-sm text-gray-600 mb-4">Receive AI-generated insights about your practice performance every week.</p>

            <div className="space-y-2">
              {[
                { value: 'weekly',       label: 'Weekly',       desc: 'Every Monday morning' },
                { value: 'twice_weekly', label: 'Twice a week', desc: 'Monday and Thursday' },
                { value: 'off',          label: 'Off',          desc: 'Dashboard only — no emails' },
              ].map(opt => (
                <label key={opt.value} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="practiceCoachFrequency"
                    value={opt.value}
                    checked={practiceCoachFrequency === opt.value}
                    onChange={() => handleSavePracticeCoachFrequency(opt.value)}
                    className="mt-0.5 w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                    <p className="text-xs text-gray-500">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>

            {coachFrequencySaving && (
              <p className="text-xs text-gray-400 mt-2">Saving…</p>
            )}
            {coachFrequencySaved && !coachFrequencySaving && (
              <p className="text-xs text-emerald-600 mt-2">✓ Saved</p>
            )}
          </div>

          {/* Last Updated Timestamp */}
          {lastUpdated && (
            <div className="text-center">
              <p className="text-xs text-slate-500">
                Last updated: {formatTimestamp(lastUpdated)}
              </p>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-center">
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`px-8 py-3 rounded-lg font-medium text-white transition-all ${
                !hasChanges || saving
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 hover:shadow-md'
              } w-full sm:w-auto`}
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </span>
              ) : (
                'Save Preferences'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
